import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from "react-router-dom";
import { Grid, Typography, Button, Container, Alert, AlertTitle, CircularProgress } from "@mui/material";
import Password from '../../common/fields/Password';
import { reset, validateResetToken } from '../../../services/LoginService';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';

export default function Reset() {
    const { hash } = useParams();
    const [tokenValid, setTokenValid] = useState(null);
    const [tokenStatus, setTokenStatus] = useState(null);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [responseReset, setResponseReset] = useState(null);
    const [errors, setErrors] = useState({
        status: {
            password: false,
            passwordConfirm: false
        },
        text: {
            password: '',
            passwordConfirm: ''
        }
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const checkToken = async () => {
            try {
                const form = { hash: hash };
                const status = await validateResetToken(form);
                if (status === 204) {
                    setTokenValid(true);
                    setTokenStatus(status)
                } else {
                    setTokenValid(false);
                    setTokenStatus(status)
                }
            } catch (error) {
                setTokenValid(false);
                setTokenStatus(500)
            }
        };

        checkToken();
    }, [hash]);

    const validatePassword = (password) => {
        const requirements = {
            length: password.length >= 12,
            uppercase: /[A-Z]/.test(password),
            lowercase: /[a-z]/.test(password),
            digit: /\d/.test(password),
            symbol: /[^0-9a-zA-Z]/.test(password)
        };

        const isValid = Object.values(requirements).every(Boolean);

        return { isValid, requirements };
    };

    const validate = () => {
        const { isValid, requirements } = validatePassword(password);
        const passwordError = password.trim() === '' || !isValid;
        const confirmError = confirmPassword.trim() === '' || password !== confirmPassword;

        let passwordMessage = '';
        if (password.trim() === '') {
            passwordMessage = 'Enter a password';
        } else if (!isValid) {
            const messages = [];
            if (!requirements.length) messages.push("at least 12 characters");
            if (!requirements.uppercase) messages.push("one uppercase letter");
            if (!requirements.lowercase) messages.push("one lowercase letter");
            if (!requirements.digit) messages.push("one number");
            if (!requirements.symbol) messages.push("one special character");
            passwordMessage = `Password must contain ${messages.join(', ')}`;
        }

        setErrors({
            status: {
                password: passwordError,
                passwordConfirm: confirmError
            },
            text: {
                password: passwordMessage,
                passwordConfirm: confirmError ? 'Passwords do not match' : ''
            }
        });

        return !passwordError && !confirmError;
    };

    const send = async () => {
        if (!validate()) return;

        const form = {
            hash,
            password,
        };

        setLoading(true);

        try {
            const { status, message } = await reset(form);
            setResponseReset({ status, message });
        } catch (err) {
            setResponseReset({
                status: 500,
                message: "We were unable to process your request."
            });
        } finally {
            setLoading(false);
        }
    };

    const login = () => {
        window.location.href = "/login";
    };

    const capitalizeFirstLetter = (str) => {
        if (!str) return '';
        return str.charAt(0).toUpperCase() + str.slice(1);
    };

    if (tokenValid === false && tokenStatus !== 204) {
        return (
            <Container maxWidth="sm" className="mx-auto">
                <Grid
                    container
                    direction="column"
                    alignItems="center"
                    justifyContent="center"
                    spacing={2}
                    sx={{ minHeight: "60vh", textAlign: "center", mt: 8 }}
                >
                    <Grid item>
                        <ErrorOutlineRoundedIcon
                            color="error"
                            sx={{ fontSize: 56 }}
                        />
                    </Grid>

                    <Grid item>
                        <Typography variant="h4" gutterBottom>
                            {tokenStatus === 404
                                ? "Invalid or expired link."
                                : "Something went wrong."}
                        </Typography>
                        <Typography variant="body1" sx={{ maxWidth: "400px", mx: "auto" }}>
                            {tokenStatus === 404
                                ? "This password reset link is no longer valid. It may have already been used or expired."
                                : "We were unable to process your request. Please try again later or contact an administrator."}

                        </Typography>
                    </Grid>

                    <Grid item>
                        <Button
                            component={RouterLink}
                            to="/recovery"
                            variant="outlined"
                            size="large"
                        >
                            Request a new reset link
                        </Button>
                    </Grid>
                </Grid>
            </Container>
        );
    }

    if (responseReset && responseReset.status !== 204) {
        return (
            <Container maxWidth="sm" className="mx-auto">
                <Grid
                    container
                    direction="column"
                    alignItems="center"
                    justifyContent="center"
                    spacing={2}
                    sx={{ minHeight: "60vh", textAlign: "center", mt: 8 }}
                >
                    <Grid item>
                        <ErrorOutlineRoundedIcon
                            color="error"
                            sx={{ fontSize: 56 }}
                        />
                    </Grid>

                    <Grid item>
                        <Typography variant="h4" gutterBottom>
                            Something went wrong.
                        </Typography>
                        <Typography variant="body1" sx={{ maxWidth: "400px", mx: "auto" }}>
                            {responseReset.status === 404
                                ? capitalizeFirstLetter(responseReset.message)
                                : "We were unable to process your request. Please try again later or contact an administrator."}
                        </Typography>
                    </Grid>

                    <Grid item>
                        <Button
                            component={RouterLink}
                            to="/recovery"
                            variant="outlined"
                            size="large"
                        >
                            Request a new reset link
                        </Button>
                    </Grid>
                </Grid>
            </Container>
        );
    }

    if (responseReset && responseReset.status === 204) {
        return (
            <Container maxWidth="sm" className="mx-auto">
                <Grid
                    container
                    direction="column"
                    alignItems="center"
                    justifyContent="center"
                    spacing={4}
                    sx={{ minHeight: "60vh", textAlign: "center", mt: 8 }}
                >
                    <Grid item>
                        <CheckCircleRoundedIcon
                            color="success"
                            sx={{ fontSize: 64 }}
                        />
                    </Grid>

                    <Grid item>
                        <Typography variant="h4" gutterBottom>
                            Password reset successful!
                        </Typography>
                        <Typography variant="body1" sx={{ maxWidth: "400px", mx: "auto" }}>
                            You can now use your new password to log in to your account.
                        </Typography>
                    </Grid>

                    <Grid item>
                        <Button
                            component={RouterLink}
                            to="/login"
                            variant="contained"
                            size="large"
                            style={{fontSize:"0.8rem",}}
                        >
                            Sign In
                        </Button>
                    </Grid>
                </Grid>
            </Container>
        );
    }

    if (tokenValid === null) {
        return (
            <Container maxWidth="md" className="mx-auto">
                <Grid
                    container
                    direction="column"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ mt: 10 }}
                    spacing={3}
                >
                    <Grid item>
                        <CircularProgress color="primary" />
                    </Grid>
                    <Grid item>
                        <Typography variant="h6" align="center">
                            Validating your password reset link...
                        </Typography>
                    </Grid>
                </Grid>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" className="mx-auto">
            <div className="login_container">
                <div className="form-header">
                    <Typography component="h1" variant="h4" className="title">Restore password</Typography>
                </div>
                <form className="form" noValidate>
                    <Grid container spacing={4}>
                        <Grid item md={6}>
                            <Password
                                id="password"
                                label="Password"
                                name="password"
                                placeholder="Enter new password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={loading}
                                error={errors.status.password}
                                errorText={errors.text.password}
                                classNameCustom="textField"
                                autoComplete="new-password"
                                required
                            />
                        </Grid>
                        <Grid item md={6}>
                            <Password
                                id="confirmPassword"
                                label="Confirm Password"
                                name="confirmPassword"
                                placeholder="Confirm new password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                disabled={loading}
                                error={errors.status.passwordConfirm}
                                errorText={errors.text.passwordConfirm}
                                classNameCustom="textField"
                                autoComplete="new-password"
                                required
                            />
                        </Grid>
                    </Grid>

                    <hr />

                    <div className="text-right">
                        <Button
                            variant="text"
                            onClick={login}
                            className="mr-1"
                            size="large"
                        >
                            Sign in to an existing account
                        </Button>

                        <Button
                            variant="contained"
                            color="primary"
                            className="submit"
                            size="large"
                            onClick={send}
                            disabled={loading}
                        >
                            {loading ? "Sending..." : "Update Password"}
                        </Button>
                    </div>
                </form>
            </div>
        </Container>
    );
}
